/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: tabs\GeneralTab.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the Provisioning Templates module.
----------------------------------------------------------------*/

import { Image, LayoutTemplate } from 'lucide-react';
import { Control, Controller, FieldErrors, UseFormRegister } from 'react-hook-form';
import { FormBox } from '../../../components/ui/FormBox';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import Select from '../../../components/ui/select';
import { useI18n } from '../../../hooks/useI18n';
import { extractLanguageOptions, extractTimezoneOptions } from '../utils/extractOptions';
import type * as api from '../api';

interface GeneralTabProps {
  control: Control<api.Template>;
  register: UseFormRegister<api.Template>;
  errors: FieldErrors<api.Template>;
  vendors: api.Vendor[];
  serverSettings: api.ServerSetting[];
  models: api.DeviceModel[];
  selectedModel: api.DeviceModel | null;
  watchedVendorId: number | undefined;
  previewImageSrc: string | null;
  handleImageError: () => void;
}

export function GeneralTab({
  control,
  register,
  errors,
  vendors,
  serverSettings,
  models,
  selectedModel,
  watchedVendorId,
  previewImageSrc,
  handleImageError,
}: GeneralTabProps) {
  const { t } = useI18n();

  return (
    <FormBox title={t('common.generalInfo', 'General Information')} icon={<LayoutTemplate className="h-4 w-4" />}>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Left Side: Fields */}
        <div className="col-span-2 grid grid-cols-2 gap-x-4 gap-y-3 items-center">
          <Label required className="text-left">{t('common.name', 'Name')}</Label>
          <Input {...register('name', { required: true })} placeholder="Template Name" error={errors.name?.message} />

          <Label required className="text-left">{t('provisioningTemplates.server', 'Server')}</Label>
          <Controller name="serverSettingsId" control={control} rules={{ required: true }}
            render={({ field }) => (
              <Select value={field.value?.toString() || ''} onValueChange={field.onChange}
                options={serverSettings.map(s => ({ value: s.id.toString(), label: `${s.name} (${s.serverDomain})` }))}
                placeholder={t('common.select', 'Select...')} />
            )} />

          <Label required className="text-left">{t('provisioningTemplates.vendor', 'Vendor')}</Label>
          <Controller name="vendorId" control={control} rules={{ required: true }}
            render={({ field }) => (
              <Select value={field.value?.toString() || ''} onValueChange={field.onChange}
                options={vendors.map(v => ({ value: v.id.toString(), label: v.name }))}
                placeholder={t('common.select', 'Select...')} searchable />
            )} />

          <Label required className="text-left">{t('provisioningTemplates.model', 'Model')}</Label>
          <Controller name="modelId" control={control} rules={{ required: true }}
            render={({ field }) => (
              <Select value={field.value?.toString() || ''} onValueChange={field.onChange}
                options={models.map(m => ({ value: m.id.toString(), label: m.model }))}
                placeholder={t('common.select', 'Select...')} disabled={!watchedVendorId} searchable />
            )} />

          <Label className="text-left">{t('provisioningTemplates.timezone', 'Timezone')}</Label>
          <Controller name="timezone" control={control}
            render={({ field }) => (
              <Select value={field.value || ''} onValueChange={field.onChange}
                options={selectedModel ? extractTimezoneOptions(selectedModel.timezone) : []}
                placeholder={selectedModel ? t('common.select', 'Select...') : t('provisioningTemplates.selectModelFirst', 'Select Model First')}
                disabled={!selectedModel} searchable />
            )} />

          <Label className="text-left">{t('provisioningTemplates.language', 'Language')}</Label>
          <Controller name="language" control={control}
            render={({ field }) => (
              <Select value={field.value || ''} onValueChange={field.onChange}
                options={selectedModel ? extractLanguageOptions(selectedModel.language) : []}
                placeholder={selectedModel ? t('common.select', 'Select...') : t('provisioningTemplates.selectModelFirst', 'Select Model First')}
                disabled={!selectedModel} searchable />
            )} />
        </div>

        {/* Right Side: Image Preview */}
        <div className="col-span-2 flex items-center justify-center">
          <div className="w-full h-full flex items-center justify-center p-6 bg-muted/10 rounded-lg border border-dashed border-border min-h-[350px]">
            {selectedModel ? (
              previewImageSrc ? (
                <div className="flex flex-col items-center">
                  <img key={previewImageSrc} src={previewImageSrc} alt={selectedModel.model}
                    className="max-h-64 object-contain" onError={handleImageError} />
                  <div className="mt-4 text-center">
                    <h4 className="font-semibold text-foreground text-lg">{selectedModel.model}</h4>
                    <p className="text-sm text-muted-foreground">{vendors.find(v => v.id === selectedModel.vendorId)?.name}</p>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center text-muted-foreground">
                  <Image className="h-16 w-16 mb-3 opacity-40" />
                  <span className="text-sm">{t('provisioningDevices.noImage', 'No image available')}</span>
                  <div className="mt-4 text-center">
                    <h4 className="font-semibold text-foreground text-lg">{selectedModel.model}</h4>
                    <p className="text-sm text-muted-foreground">{vendors.find(v => v.id === selectedModel.vendorId)?.name}</p>
                  </div>
                </div>
              )
            ) : (
              <div className="flex flex-col items-center text-muted-foreground">
                <Image className="h-16 w-16 mb-3 opacity-30" />
                <span className="text-sm">{t('provisioningTemplates.selectModelFirst', 'Select a model to preview')}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </FormBox>
  );
}
